import { Injectable, signal } from '@angular/core';
import { ListingDetail } from '../models/listing-detail.model';
import { track } from './analytics';

@Injectable({ providedIn: 'root' })
export class ListingDetailService {
  readonly openedId = signal<number | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly detail = signal<ListingDetail | null>(null);

  private readonly cache = new Map<number, ListingDetail>();
  private inflightId: number | null = null;

  open(id: number): void {
    this.openedId.set(id);
    track('planner_pin_open', { listing_id: id });
    void this.load(id);
  }

  close(): void {
    this.openedId.set(null);
    this.inflightId = null;
    this.detail.set(null);
    this.loading.set(false);
    this.error.set(null);
  }

  retry(): void {
    const id = this.openedId();
    if (id != null) void this.load(id);
  }

  private async load(id: number): Promise<void> {
    const cached = this.cache.get(id);
    if (cached) {
      this.detail.set(cached);
      this.loading.set(false);
      this.error.set(null);
      this.inflightId = null;
      return;
    }

    this.inflightId = id;
    this.detail.set(null);
    this.error.set(null);
    this.loading.set(true);

    try {
      const res = await fetch(`/api/v1/listings/${id}/detail`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as ListingDetail;
      this.cache.set(id, body);
      if (this.inflightId !== id) return;
      this.detail.set(body);
    } catch (e) {
      if (this.inflightId !== id) return;
      this.error.set(e instanceof Error ? e.message : 'Greska pri ucitavanju.');
    } finally {
      if (this.inflightId === id) {
        this.loading.set(false);
        this.inflightId = null;
      }
    }
  }
}
